import React, { useState } from 'react';
import { Box } from '@mui/material';
import {
  ChevronLeft as BackIcon,
  MicNone as MicIcon,
  NotificationsNone as BellIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import FinMark from '../brand/FinMark';
import { Initial } from './primitives';
import { richText } from './richText';
import { landing, people } from './tokens';

type Mode = 'personal' | 'group';

const bubbleBase = {
  px: 1.625,
  py: 1.125,
  fontSize: 14.5,
  lineHeight: 1.45,
};

/** A message the visitor sends: solid petrol, on the right. */
const Mine: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      ...bubbleBase,
      alignSelf: 'flex-end',
      maxWidth: '78%',
      borderRadius: '18px 18px 5px 18px',
      bgcolor: landing.accent,
      color: '#FFFFFF',
    }}
  >
    {children}
  </Box>
);

/**
 * The buttons under Fin's reading of a message. Fin saves nothing until one
 * is pressed, so the demo shows them every time, as the bot does.
 */
const Keys: React.FC<{ labels: string[] }> = ({ labels }) => (
  <Box component='span' sx={{ display: 'flex', gap: 0.75, mt: 1 }}>
    {labels.map((label, index) => (
      <Box
        key={label}
        component='span'
        sx={{
          flex: 1,
          textAlign: 'center',
          py: 0.6,
          px: 1,
          borderRadius: '10px',
          bgcolor: index === 0 ? landing.accent : landing.surface,
          color: index === 0 ? '#FFFFFF' : landing.accent,
          border: `1px solid ${landing.accentSoft2}`,
          fontSize: 13,
          fontWeight: 600,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Box>
    ))}
  </Box>
);

/** A message from Fin or a group member: on the left, optionally named. */
const Theirs: React.FC<{
  children: React.ReactNode;
  name?: string;
  nameColor?: string;
  fin?: boolean;
  keys?: string[];
}> = ({ children, name, nameColor, fin = false, keys }) => (
  <Box
    sx={{
      alignSelf: 'flex-start',
      maxWidth: '86%',
      display: 'flex',
      flexDirection: 'column',
      gap: 0.4,
    }}
  >
    {name && (
      <Box
        component='span'
        sx={{ fontSize: 12, fontWeight: 700, color: nameColor, pl: 0.5 }}
      >
        {name}
      </Box>
    )}
    <Box
      sx={{
        ...bubbleBase,
        borderRadius: '18px 18px 18px 5px',
        bgcolor: fin ? landing.accentSoft : landing.surface,
        border: `1px solid ${fin ? landing.accentSoft2 : landing.line}`,
        color: landing.ink,
      }}
    >
      {children}
      {keys && <Keys labels={keys} />}
    </Box>
  </Box>
);

const waveform = [8, 15, 21, 12, 18, 24, 10, 16, 20, 11, 7];

const PersonalChat: React.FC = () => {
  const { t } = useTranslation('landing');
  const keys = [t('chat.keys.confirm'), t('chat.keys.edit')];
  return (
    <>
      <Mine>{t('chat.personal.lunch')}</Mine>
      <Theirs keys={keys}>{richText(t('chat.personal.lunchReply'))}</Theirs>
      <Theirs>{t('chat.saved')}</Theirs>
      <Box
        aria-label={t('chat.voiceLabel')}
        role='img'
        sx={{
          alignSelf: 'flex-end',
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          py: 1,
          pl: 1,
          pr: 1.75,
          borderRadius: '18px 18px 5px 18px',
          bgcolor: landing.accent,
          color: '#FFFFFF',
        }}
      >
        <Box
          component='span'
          sx={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,.22)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& svg': { fontSize: 18 },
          }}
        >
          <PlayIcon />
        </Box>
        <Box component='span' sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
          {waveform.map((height, index) => (
            <Box
              key={index}
              component='span'
              sx={{
                width: 3,
                height,
                borderRadius: '2px',
                bgcolor: index < 7 ? '#FFFFFF' : 'rgba(255,255,255,.6)',
              }}
            />
          ))}
        </Box>
        <Box component='span' sx={{ fontSize: 12.5 }}>
          {t('chat.voiceDuration')}
        </Box>
      </Box>
      <Theirs keys={keys}>{richText(t('chat.personal.voiceReply'))}</Theirs>
      <Box
        sx={{
          alignSelf: 'flex-end',
          width: 156,
          p: 0.75,
          borderRadius: '18px 18px 5px 18px',
          bgcolor: landing.accent,
        }}
      >
        <Box
          role='img'
          aria-label={t('chat.receiptLabel')}
          sx={{
            bgcolor: '#FFFFFF',
            color: '#1F2A2E',
            borderRadius: '12px',
            p: '10px 11px 12px',
            fontFamily: landing.mono,
            fontSize: 10,
            lineHeight: 1.55,
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}
        >
          <Box component='span' sx={{ fontWeight: 700, textAlign: 'center' }}>
            {t('chat.personal.receiptStore')}
          </Box>
          <Box component='span' sx={{ textAlign: 'center', color: landing.ink3 }}>
            25/10/2026 08:12
          </Box>
          <Box
            component='span'
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 0.5,
              pt: 0.6,
              borderTop: '1px dashed #B9C3C6',
            }}
          >
            <span>{t('chat.personal.receiptItem')}</span>
            <span>180,00</span>
          </Box>
          <Box
            component='span'
            sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}
          >
            <span>TOTAL</span>
            <span>R$ 180,00</span>
          </Box>
        </Box>
      </Box>
      <Theirs keys={keys}>{richText(t('chat.personal.receiptReply'))}</Theirs>
      <Box
        sx={{
          ...bubbleBase,
          alignSelf: 'flex-start',
          maxWidth: '88%',
          display: 'flex',
          gap: 1.25,
          alignItems: 'flex-start',
          borderRadius: '18px 18px 18px 5px',
          bgcolor: landing.accentSoft,
          border: `1px solid ${landing.accentSoft2}`,
          color: landing.ink,
          '& svg': { fontSize: 19, color: landing.accent, mt: '1px' },
        }}
      >
        <BellIcon />
        <span>{richText(t('chat.personal.reminder'))}</span>
      </Box>
    </>
  );
};

const GroupChat: React.FC = () => {
  const { t } = useTranslation('landing');
  const fin = t('chat.finName');
  const balances = ['balance1', 'balance2', 'balance3'];
  const keys = [t('chat.keys.confirm'), t('chat.keys.edit')];
  return (
    <>
      <Theirs name='Bruna' nameColor={people.bruna.color}>
        {t('chat.group.m1')}
      </Theirs>
      <Theirs name={fin} nameColor={landing.accent} fin keys={keys}>
        {richText(t('chat.group.r1'))}
      </Theirs>
      <Theirs name={fin} nameColor={landing.accent} fin>
        {t('chat.saved')}
      </Theirs>
      <Theirs name='Diego' nameColor={people.diego.color}>
        {t('chat.group.m2')}
      </Theirs>
      <Theirs name={fin} nameColor={landing.accent} fin keys={keys}>
        {richText(t('chat.group.r2'))}
      </Theirs>
      <Mine>{t('chat.group.m3')}</Mine>
      <Theirs name={fin} nameColor={landing.accent} fin keys={[t('chat.keys.record'), t('chat.keys.cancel')]}>
        {richText(t('chat.group.r3'))}
      </Theirs>
      <Mine>{t('chat.group.m4')}</Mine>
      <Theirs name={fin} nameColor={landing.accent} fin>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9, minWidth: 220 }}>
          <Box
            component='span'
            sx={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: landing.ink3,
            }}
          >
            {t('chat.group.balanceTitle')}
          </Box>
          {balances.map((key) => (
            <Box
              key={key}
              sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, fontSize: 14 }}
            >
              <span>{t(`chat.group.${key}`)}</span>
              <strong>{t(`chat.group.${key}Amount`)}</strong>
            </Box>
          ))}
        </Box>
      </Theirs>
    </>
  );
};

const tabSx = (active: boolean) => ({
  minHeight: 40,
  px: 2.25,
  borderRadius: '999px',
  border: 0,
  font: 'inherit',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  bgcolor: active ? landing.accent : 'transparent',
  color: active ? '#FFFFFF' : landing.ink2,
  transition: 'background-color 150ms ease, color 150ms ease',
});

/** The hero phone: the same Fin, alone with you or inside a group. */
const ChatDemo: React.FC = () => {
  const { t } = useTranslation('landing');
  const [mode, setMode] = useState<Mode>('personal');
  const isGroup = mode === 'group';

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2.25,
      }}
    >
      <Box
        role='group'
        aria-label={t('chat.tabsLabel')}
        sx={{
          display: 'inline-flex',
          gap: 0.5,
          p: 0.5,
          borderRadius: '999px',
          bgcolor: landing.surface,
          border: `1px solid ${landing.line}`,
        }}
      >
        <Box
          component='button'
          type='button'
          aria-pressed={!isGroup}
          onClick={() => setMode('personal')}
          sx={tabSx(!isGroup)}
        >
          {t('chat.tabPersonal')}
        </Box>
        <Box
          component='button'
          type='button'
          aria-pressed={isGroup}
          onClick={() => setMode('group')}
          sx={tabSx(isGroup)}
        >
          {t('chat.tabGroup')}
        </Box>
      </Box>

      <Box
        component='figure'
        aria-label={t('chat.phoneLabel')}
        sx={{
          m: 0,
          width: '100%',
          maxWidth: 360,
          p: 1.25,
          borderRadius: '46px',
          bgcolor: '#0B1214',
          boxShadow: landing.shadow,
          border: '1px solid rgba(255,255,255,.08)',
        }}
      >
        <Box
          sx={{
            height: 720,
            borderRadius: '36px',
            overflow: 'hidden',
            bgcolor: landing.chatBg,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: '20px 16px 12px',
              bgcolor: landing.surface,
              borderBottom: `1px solid ${landing.line}`,
            }}
          >
            <BackIcon sx={{ color: landing.accent }} aria-hidden='true' />
            {isGroup ? (
              <>
                <Box sx={{ display: 'flex' }}>
                  <Initial letter='B' {...people.bruna} />
                  <Initial letter='D' {...people.diego} overlap />
                  <Initial letter='A' {...people.ana} overlap />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box component='span' sx={{ fontWeight: 700, fontSize: 15 }}>
                    {t('chat.groupName')}
                  </Box>
                  <Box component='span' sx={{ fontSize: 12.5, color: landing.ink3 }}>
                    {t('chat.groupMembers')}
                  </Box>
                </Box>
              </>
            ) : (
              <>
                <FinMark size={38} />
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box component='span' sx={{ fontWeight: 700, fontSize: 15 }}>
                    {t('chat.finName')}
                  </Box>
                  <Box component='span' sx={{ fontSize: 12.5, color: landing.ink3 }}>
                    {t('chat.finSubtitle')}
                  </Box>
                </Box>
              </>
            )}
          </Box>

          {/* column-reverse keeps the latest message in view, like a real
              chat, and still lets the visitor scroll up to the start. */}
          <Box
            tabIndex={0}
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              display: 'flex',
              flexDirection: 'column-reverse',
              px: 1.5,
              py: 2,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {isGroup ? <GroupChat /> : <PersonalChat />}
            </Box>
          </Box>

          <Box
            aria-hidden='true'
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: '10px 12px 16px',
              bgcolor: landing.surface,
              borderTop: `1px solid ${landing.line}`,
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 42,
                borderRadius: '999px',
                bgcolor: landing.bg2,
                display: 'flex',
                alignItems: 'center',
                px: 2,
                fontSize: 14,
                color: landing.ink3,
              }}
            >
              {t('chat.inputPlaceholder')}
            </Box>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: '50%',
                bgcolor: landing.accent,
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MicIcon sx={{ fontSize: 20 }} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatDemo;
